<template>
  <flow-layout :current-step-index="isBrokerFlow ? 3 : 2">
    <heading level="2">{{
      isBrokerFlow ? $t('moreBusinessInfo.brokerTitle') : $t('moreBusinessInfo.title')
    }}</heading>
    <form autocomplete="off">
      <input-inline>
        <established-date
          id="business-established-date"
          name="business-established-year"
          @input="checkFormValidation"
          v-model="business.establishedDate"
          :maxlength="ESTABLISHED_DATE_MAX_LENGTH"
          :placeholder="$t('moreBusinessInfo.businessEstablished.yearPlaceholder')"
          v-validation="[['dynamic', '/business/establishedDate']]"
        >
          <template #label>{{
            isBrokerFlow
              ? $t('moreBusinessInfo.businessEstablished.brokerYearLabel')
              : $t('moreBusinessInfo.businessEstablished.yearLabel')
          }}</template>
        </established-date>
      </input-inline>

      <combined-element v-if="isShowEstablishedMonth">
        <input-inline>
          <input-select
            id="business-established-month"
            name="business-established-month"
            v-model="business._extension.establishedMonth"
            @change="changeMonth"
            @input="checkFormValidation"
            :defaultValue="$t('select')"
            :options="monthOptions"
            v-validation:closed="[['dynamic', '/business/_extension/establishedMonth']]"
          >
            <template #label>{{
              isBrokerFlow
                ? $t('moreBusinessInfo.businessEstablished.brokerMonthLabel')
                : $t('moreBusinessInfo.businessEstablished.monthLabel')
            }}</template>
          </input-select>
        </input-inline>
      </combined-element>

      <input-inline>
        <input-money
          id="gross-monthly-revenue"
          name="gross-monthly-revenue"
          v-model="business._extension.averageMonthlyRevenue"
          @input="checkFormValidation"
          :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
          :maxlength="AVERAGE_MONTHLY_REVENUE_MAX_LENGTH"
          :placeholder="$t('moreBusinessInfo.averageMonthlyRevenue.placeholder')"
          v-validation="[['dynamic', '/business/_extension/averageMonthlyRevenue']]"
        >
          <template #label>
            {{
              isBrokerFlow
                ? $t('moreBusinessInfo.averageMonthlyRevenue.brokerLabel')
                : $t('moreBusinessInfo.averageMonthlyRevenue.label')
            }}
          </template>
          <template #prefix>{{ $t('moreBusinessInfo.averageMonthlyRevenue.prefix') }}</template>
        </input-money>
      </input-inline>

      <input-inline>
        <input-percent
          id="percent-ownership"
          name="percent-ownership"
          :value="owner.percentOwnership"
          @input="
            checkFormValidation();
            owner = { ...owner, percentOwnership: $event };
          "
          aria-labelledby="ownership-percentage-column-header"
          v-validation="[['dynamic', '/owners/*/percentOwnership']]"
        >
          <template #label>
            {{
              isBrokerFlow
                ? $t('moreBusinessInfo.ownershipPercentage.brokerLabel')
                : $t('moreBusinessInfo.ownershipPercentage.label')
            }}
          </template>
          <template #suffix>
            {{ $t('moreBusinessInfo.ownershipPercentage.suffix') }}
          </template>
        </input-percent>
      </input-inline>

      <media-object class="lowPercentageText" v-if="isPercentageIsLow">
        <svg-use slot="icon" name="Icons_Business_Group of people" class="icon" />
        <template #body>
          {{ $t('moreBusinessInfo.lowPercentageText') }}
        </template>
      </media-object>

      <navigation-buttons
        @next="submit"
        :next="{
          label: $t('nextButtonLabel'),
          modifiers: { disabled: !isFormValid },
        }"
        @back="goBack"
      />
    </form>
  </flow-layout>
</template>

<script>
import applicationMixin from '@/mixins/application';
import brokerFlowMixin from '@/mixins/broker-flow';
import FlowLayout from '@/views/layouts/FlowLayout';
import InputInline from '@/components/InputInline';
import CombinedElement from '@/components/CombinedElement';
import InputPercent from '@/components/InputPercent';
import EstablishedDate from '@/components/EstablishedDate';
import {
  ESTABLISHED_DATE_MAX_LENGTH,
  AVERAGE_MONTHLY_REVENUE_MAX_LENGTH,
} from '@/modules/validation-service/fields-length';
import months from '@/modules/months';

import mapConstsToOptions from '@/utils/map-consts-to-options';

const MIN_YEAR_BUSINESS_ESTABLISHED = 2;

export default {
  name: 'more-business-info',
  components: { FlowLayout, InputInline, CombinedElement, InputPercent, EstablishedDate },
  data() {
    return {
      ESTABLISHED_DATE_MAX_LENGTH,
      AVERAGE_MONTHLY_REVENUE_MAX_LENGTH,
      isFormValid: false,
    };
  },
  computed: {
    business: {
      get() {
        return this.application.business;
      },
      set(value) {
        this.application = { ...this.application, business: value };
      },
    },
    owner: {
      get() {
        return this.application.owners[0];
      },
      set(value) {
        this.application = {
          ...this.application,
          owners: [value, ...this.application.owners.slice(1)],
        };
      },
    },
    isPercentageIsLow() {
      return this.owner.percentOwnership && this.owner.percentOwnership < 50;
    },
    monthOptions() {
      const formattedEstablishedDate = new Date(this.business.establishedDate);
      if (new Date().getUTCFullYear() === formattedEstablishedDate.getUTCFullYear()) {
        return mapConstsToOptions(months, 'months').slice(0, new Date().getMonth() + 1);
      }
      return mapConstsToOptions(months, 'months');
    },
    isShowEstablishedMonth() {
      const [year] = this.business.establishedDate
        ? this.business.establishedDate.toString().split('-')
        : '';
      if (year && year.length === ESTABLISHED_DATE_MAX_LENGTH) {
        const yearNow = new Date().getFullYear();
        if (yearNow - year < 0) {
          return false;
        }
        return yearNow - year <= MIN_YEAR_BUSINESS_ESTABLISHED;
      }
      return false;
    },
  },
  methods: {
    changeMonth() {
      this.$nextTick().then(() => {
        if (this.business._extension.establishedMonth !== undefined) {
          const monthNum = this.monthNumberFromString(this.business._extension.establishedMonth);
          const formattedYear = new Date(this.business.establishedDate);
          const getYear = formattedYear.getUTCFullYear();
          const result = `${getYear}-${monthNum}-01`;
          this.business.establishedDate = result;
        }
      });
    },
    monthNumberFromString(str) {
      const indexMonth = months.indexOf(str) + 1;
      return indexMonth.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
    },
    checkFormValidation() {
      this.$nextTick().then(() => {
        this.isFormValidFn();
      });
    },
    isFormValidFn() {
      let validationCounter = 0;
      this.isFormValid = false;
      this.$validations.collections[0].forEach((value, key) => {
        if (key === 'business-established-date' && value.component.value) {
          validationCounter += 1;
        } else if (value.component.value && value.component.errorMessage === '') {
          validationCounter += 1;
        }
      });
      this.isFormValid = validationCounter === this.$validations.collections[0].size;
    },
  },
  mixins: [applicationMixin, brokerFlowMixin],
};
</script>

<style lang="sass" scoped>
.combinedElement
  margin-top: $glass-spacing-2
  padding-top: 0;

.lowPercentageText
  +font-size(13.33, 16)
  margin: $glass-spacing-3 0
  padding: $glass-spacing-2
  color: #000000
  background-color: $glass-color-brand-secondary-accent-01
</style>
